import './ViewDialog.scss';

import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import useMediaQuery from '../../../../../../hooks/useMediaQuery';
import { WithData } from '../../../../../../types/api';
import { EntityIdRouteParams } from '../../../../../../types/routing';
import DialogSpinner from '../../../../../Dialogs/DialogSpinner/DialogSpinner';
import {
  SortingMapsPortsType,
  SortingMapsType,
} from '../../SortingMaps.functions';

type Props = {
  isShown: boolean;
  onHide: () => void;
  data: SortingMapsType | undefined;
  isEditBtnShown: boolean;
  setIsEditDialog: (isEditDialog: SortingMapsType) => void;
};

function ViewDialogMaps({
  isShown,
  onHide,
  data,
  isEditBtnShown,
  setIsEditDialog,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { id } = useParams<EntityIdRouteParams>();
  const isOnMobile = useMediaQuery('(max-width: 768px)');

  const { data: portsData, isLoading } = useAxiosHook<
    WithData<SortingMapsPortsType[]>
  >(`/orders/sorting/machine/${id}/map/${data?.id}/port`, {
    skipWhen: !isShown || !data?.id || !id,
  });

  const pointTypes = useMemo(
    () => [
      { value: 1, label: t('Warehouse') },
      { value: 2, label: t('Drop point') },
      { value: 3, label: t('Locker') },
    ],
    [t]
  );

  const dialogFooter = !isLoading && (
    <>
      {isEditBtnShown && (
        <Button
          label={t('Edit')}
          className="p-button-text"
          onClick={() => setIsEditDialog(data!)}
        />
      )}
      <Button label={t('Close')} onClick={() => onHide()} />
    </>
  );

  return (
    <Dialog
      header={isLoading ? t('Loading...') : t('Preview on sorting maps')}
      footer={dialogFooter}
      visible={isShown}
      resizable={false}
      onHide={onHide}
      maximizable
      maximized={isOnMobile}
      style={{ width: 580, maxWidth: '100%' }}
      className="sorting-maps-view-dialog"
    >
      {isLoading ? (
        <DialogSpinner />
      ) : (
        <>
          <span className="section-title">{t('General information')}</span>
          <table cellPadding="0" cellSpacing="0">
            <tbody>
              <tr>
                <td>{t('Sorting map name')}</td>
                <td className="route-name">{data?.name}</td>
              </tr>
              <tr>
                <td>{t('Description')}</td>
                <td className="route-name">{data?.description}</td>
              </tr>
              <tr>
                <td>{t('Error Port')}</td>
                <td className="route-name">{data?.errorPort}</td>
              </tr>
              <tr>
                <td>{t('Unmatched Port')}</td>
                <td className="route-name">{data?.unmatchedPort}</td>
              </tr>
            </tbody>
          </table>
          <span className="section-title">{t('Ports')}</span>
          {portsData && portsData.data && portsData.data?.length > 0 ? (
            <table cellPadding="0" cellSpacing="0">
              <thead>
                <tr>
                  <th>{t('Port ID')}</th>
                  <th>{t('Type')}</th>
                  <th>{t('Port')}</th>
                </tr>
              </thead>
              <tbody>
                {portsData?.data?.map((port) => (
                  <tr key={port.id}>
                    <td>{port.port}</td>
                    <td>
                      {pointTypes.find(
                        (p) => p.value === port.logisticsPointTypeId
                      )?.label ?? '-'}
                    </td>

                    <td>{port.logisticsPointName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <span>{t('No ports found')}</span>
          )}
        </>
      )}
    </Dialog>
  );
}

export default ViewDialogMaps;
